<template>
  <transition name="fade">
    <button
      v-if="
        Boolean(matchingProduct) &&
          Boolean(matchingProduct.title) &&
          !isUpsellInCart &&
          Boolean(metafield.product)
      "
      class="product-matching-set text-left"
      :class="[
        `product-matching-set--${modifier}`
      ]"
      type="button"
      @click="openQuickShop"
    >
      <span class="product-matching-set__info">
        <span
          v-if="modifier === 'inline'"
          class="product-matching-set__cta text-body-2xs-regular"
          v-text="metafield.button"
        />

        <span
          class="text-body-2xs-regular"
          v-html="metafield.description"
        />

        <btn
          v-if="modifier === 'block'"
          modifiers="secondary center"
          :label="metafield.button"
          custom-element="span"
          class="product-matching-set__cta--block"
        />
      </span>

      <transition
        name="fade"
        mode="out-in"
      >
        <responsive-image
          :key="matchingProduct.handle"
          class="product-matching-set__image"
          :image="image"
          :ratio="ratio"
          sizes="(min-width: 1024px) 10vw, 33vw"
        />
      </transition>
    </button>
  </transition>
</template>

<script>

/**
 * Vue: Product matching set (product-matching-set)
 * -----------------------------------------------------------------------------
 * Up-sell a matching product when a product is added to cart and when the mini
 * cart is viewed.
 *
 * @param {String} [modifier] - Use "block" to display block button with
 * border. "inline" to display inline button without desktop border.
 * @param {Boolean|Object|String} product - Product that may have upsell
 * metafields. Can also provide a product handle to query a product.
 */

import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'

import productQuery from '~graphql/product.gql'

import { formatProduct } from '~/format/format-product'

export default {
  name: 'ProductMatchingSet',

  components: {
    Btn,
    ResponsiveImage,
  },

  props: {
    modifier: {
      type: String,
      default: 'inline',
    },
    placement: {
      type: String,
      default: '',
    },
    product: {
      type: [Boolean, Object, String],
      default: false,
    },
  },

  emits: ['shows-upsell'],

  data() {
    return {
      stateProduct: this.product,
      matchingProduct: false,
      namespace: `${new Date().getTime()}-matching-set`,
      isUpsellInCart: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      cart: (state) => state.cart,
    }),

    /**
     * Compute first image from matching product
     */
    image() {
      return this.matchingProduct?.selectedOrFirstAvailableVariant?.image || this.matchingProduct?.media?.find(media => media.mediaType === 'image')?.image || false
    },

    /**
     * Compute matching set metafield
     */
    metafield() {
      const meta = {
        ...this.stateProduct?.metafields?.matchingSet || {},
      }

      if (meta.button) {
        meta.button = meta.button.charAt(0).toUpperCase() + meta.button.slice(1).toLowerCase()
      }

      return meta
    },

    /**
     * Compute ratio according to mobile / desktop and modifier
     */
    ratio() {
      const ratios = {
        mobile: {
          block: '104:132',
          inline: '113:152',
        },
        block: '111:153',
        inline: '116:151',
      }

      if (this.isMobile) {
        return ratios.mobile[this.modifier] || ''
      }

      return ratios[this.modifier] || ''
    },
  },

  watch: {

    /**
     * Watch main product's metafields to get matching product via handle
     */
    'metafield.product': {
      handler(current, previous) {
        this.getProductViaHandle(current, previous)
      },
      immediate: true,
    },

    /**
     * Watch cart change to get matching product via handle
     */
    'cart.status': {
      handler() {
        if (this.cart.status.label === 'updated') {
          setTimeout(() => {
            this.getProductViaHandle(this.metafield.product, null)
          }, this.$timing('slow'))
        }
      },
      immediate: true,
    },

    /**
     * Watch product prop to update main product
     */
    product: {
      async handler(current, previous) {
        if (
          Boolean(current) &&
          typeof current === 'string' &&
          current !== previous
        ) {
          this.stateProduct = await this.queryProduct(current)
          return
        }
        this.stateProduct = current
      },
      immediate: true,
    },
  },

  methods: {

    /**
     * Overlay actions
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      setProductFormDrawerProduct: 'productFormDrawer/setProductFormDrawerProduct',
      setConfig: 'productFormDrawer/setConfig',
    }),

    openQuickShop() {
      const pageTypeVariable = this.$variable('template.name')
      const pageTypeObject = {
        product: 'PDP',
        collection: 'PLP',
      }
      const mainSku = this.stateProduct.variants[0].sku
      const config = {
        strings: {
          return: this.$string('product.matching_set.back'),
          back: '',
          label: this.metafield.button,
        },
        bisNamespace: 'matching-set',
        location: {
          key: `makeItASet_${this.placement}`,
          _upsell: `${mainSku}_#added_sku#_${this.placement || pageTypeObject[pageTypeVariable]}`,
        },
      }

      if (this.placement === 'mini-cart') {
        config.focusReturn = 'button.product-matching-set--block'
      }
      if (this.placement === 'quickAddSuccess') {
        config.focusReturn = '.site-header__cart.icon-button'
      }

      this.setProductFormDrawerProduct(this.matchingProduct)
      this.setConfig(config)

      this.closeOverlay('quick-add-to-cart-modal')
      this.closeOverlay('productFormSuccessMobile')
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'product-form-drawer',
      })
    },

    /**
     * Query a product using storefront API
     * @param {String} handle - Product handle
     */
    async queryProduct(handle) {
      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: {
            country: this.$variable('store.country'),
            handle,
            language: this.$variable('store.language'),
          },
        })

        return formatProduct(response)
      } catch (error) {
        cnvs.ReportError('[Product Matching Set]', error)
        return false
      }
    },

    /**
     * Get matching product from metafield
     */
    async getProductViaHandle(current, previous) {
      if (!current) {
        this.matchingProduct = false
        return
      }
      if (current === previous) {
        return
      }
      this.matchingProduct = await this.queryProduct(current)
      this.checkUpsellInCart()

      this.$emit('shows-upsell', this.isUpsellInCart)
    },

    /**
     * Check if upsell product is in cart
     */
    checkUpsellInCart() {
      this.isUpsellInCart = this.cart.response.items.some((item) => {
        return this.matchingProduct.variants.some((variant) => variant.id === item.id)
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-matching-set';
</style>
